// The icon library's uploaded-icon previews, out of CustomizerScreen (backfill PR 31): the
// previews GET /api/user-icons/previews lists for the signed-in account's own icons and, by
// id, for the icons the open design shows (another account's, when an admin opens their
// design), merged by id into one list, with the loading flag of the uploaded-icons tab, and
// refreshed when the tab becomes visible again and a preview link is about to expire.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { toastRequestError } from '../utils';
import { SIGNED_URL_REFRESH_BUFFER_MS } from './useUserIconAssets';

const mergePreviewsById = (prev, data) => {
  const previewsById = new Map(
    prev.map((preview) => [String(preview._id), preview])
  ); // the previews already loaded, by id (the own library and the ones the open design references)

  data.forEach((preview) => {
    previewsById.set(String(preview._id), preview);
  }); // add or replace the previews the route answered with

  return [...previewsById.values()];
}; // one list of both answers, each icon once, the later answer's object (both fetches below held this reducer until a PR 31 review)

export function useUserIconPreviews({ userInfo, usedUserIconAssetIds, t }) {
  const [userIconPreviews, setUserIconPreviews] = useState([]);
  const [loadingUserIcons, setLoadingUserIcons] = useState(false); // whether the uploaded icon library is currently being fetched

  const usedUserIconAssetIdsKey = useMemo(
    () => [...usedUserIconAssetIds].sort().join('|'),
    [usedUserIconAssetIds]
  ); // creates a stable dependency key for uploaded icon ids used in the design

  const fetchUserIconPreviews = useCallback(async () => {
    if (!userInfo) {
      return;
    }

    setLoadingUserIcons(true); // show the loading indicator in the uploaded icons tab

    try {
      const { data } = await api.get('/api/user-icons/previews');

      setUserIconPreviews((prev) => mergePreviewsById(prev, data)); // the signed-in user's own uploaded icon library, over the previews loaded because the currently opened design references them (fetchReferencedUserIconPreviews)
    } catch (err) {
      toastRequestError(err, t); // the route's message (the fallback that stood here never showed: deleted in PR 31, user decision)
    } finally {
      setLoadingUserIcons(false); // hide the loading indicator after the request finishes
    }
  }, [userInfo]); // fetch uploaded icon previews for the signed-in user's own library and merge them into the preview cache

  const fetchReferencedUserIconPreviews = useCallback(async () => {
    if (!userInfo || usedUserIconAssetIds.length === 0) {
      return; // do not fetch referenced uploaded icon previews if no user is signed in or the current design does not use uploaded icons
    }

    try {
      const { data } = await api.get('/api/user-icons/previews', {
        params: {
          assetIds: usedUserIconAssetIds.join(','),
        },
      }); // fetch preview data for the uploaded icons referenced by the currently opened design

      setUserIconPreviews((prev) => mergePreviewsById(prev, data)); // merge referenced uploaded icon previews into the existing preview cache (including icons from another user if the current user is an admin)
    } catch (err) {
      toastRequestError(err, t); // the route's message (the fallback that stood here never showed: deleted in PR 31, user decision)
    }
  }, [userInfo, usedUserIconAssetIdsKey]); // this function loads previews by the assetIds used in the currently opened design, so admins can preview another user's uploaded icons when inspecting or editing that user's design. The fetchUserIconPreviews only loads the signed-in user's own uploaded icon library.

  useEffect(() => {
    fetchReferencedUserIconPreviews();
  }, [fetchReferencedUserIconPreviews]); // load previews for uploaded icons referenced by the currently opened design

  const userIconPreviewsById = useMemo(
    () =>
      Object.fromEntries(
        userIconPreviews.map((userIconPreview) => [
          String(userIconPreview._id),
          userIconPreview,
        ])
      ),
    [userIconPreviews]
  ); // creates a fast lookup for user uploaded icon previews by their assetId

  useEffect(() => {
    fetchUserIconPreviews();
  }, [fetchUserIconPreviews]); // load uploaded icon previews when the user is available

  const previewRefreshInFlightRef = useRef(false);

  const refreshAllRelevantUserIconPreviewsOnce = useCallback(async () => {
    if (previewRefreshInFlightRef.current) {
      return; // prevent multiple simultaneous preview refresh requests
    }

    previewRefreshInFlightRef.current = true;

    try {
      await Promise.all([
        fetchUserIconPreviews(),
        fetchReferencedUserIconPreviews(),
      ]); // refresh both the signed-in user's own icon library and previews referenced by the currently opened design
    } finally {
      previewRefreshInFlightRef.current = false;
    }
  }, [fetchUserIconPreviews, fetchReferencedUserIconPreviews]); // refresh all preview URLs that may be displayed in the Customizer

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!userInfo) {
        return; // don't fetch user icon previews if no user is logged in
      }

      if (document.visibilityState !== 'visible') {
        return; // only check preview URLs when the tab becomes visible again
      }

      const hasExpiringPreviewUrl = userIconPreviews.some((preview) => {
        if (!preview.previewUrlExpiresAt) {
          return true; // if no expiry info exists, refresh defensively
        }

        return (
          Date.now() >
          Number(preview.previewUrlExpiresAt) - SIGNED_URL_REFRESH_BUFFER_MS
        ); // refresh previews if at least one preview URL is close to expiring
      });

      if (hasExpiringPreviewUrl) {
        refreshAllRelevantUserIconPreviewsOnce(); // refresh own-library previews and design-referenced previews, including other users' icons for admins
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange); // run the check when the user returns to the tab

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange); // cleanup the event listener
    };
  }, [userInfo, userIconPreviews, refreshAllRelevantUserIconPreviewsOnce]); // refreshes uploaded icon preview URLs when the tab becomes visible again

  return {
    userIconPreviews,
    setUserIconPreviews,
    userIconPreviewsById,
    loadingUserIcons,
    fetchUserIconPreviews,
    refreshAllRelevantUserIconPreviewsOnce,
  };
} // the icon library's preview list and its refreshes
